/**
 * Último toque de marketing del navegador, para atribuir cada COMPRA.
 *
 * `purchase_completed` se emite en el webhook, sin navegador: no tiene referrer
 * ni UTM propios. Este módulo apunta de dónde llegó el visitante al cargar la
 * página (`recordLandingVisit`, desde `instrumentation-client.ts`) y el checkout
 * lo manda al servidor (`checkoutAttributionField`), que lo guarda en la
 * metadata del proveedor de pago hasta que vuelve con el webhook.
 *
 * Regla: último toque NO directo, con caducidad de 30 días. Una campaña (UTM o
 * `gclid`) o un referrer externo sustituyen al toque guardado; una visita
 * directa —o la vuelta del checkout o de un login de terceros— no lo pisa.
 *
 * Consentimiento: es analítica, así que nada sale de la memoria de la página
 * sin consentimiento de cookies. Si se consiente después de aterrizar,
 * `persistMarketingAttribution` guarda la landing de esta carga; si se rechaza
 * o se resetea, lo guardado se borra. Esas transiciones son de `useCookieConsent`.
 */
import { STORAGE_KEYS, getStorageItem, removeStorageItem, setStorageItem } from '@/lib/pwa-storage'
import { classifyAnalyticsUrl, sanitizeAnalyticsUrl } from '@/lib/analytics-url-policy'
import { hasStoredAnalyticsConsent } from '@/lib/cookie-consent-status'
import { parseMarketingAttribution, type MarketingAttribution } from '@/server/domain/marketing-attribution'

/** Lo que el módulo necesita de una carga de página. */
export interface LandingVisit {
  pathname: string
  search: string
  /** Sin puerto, como el `hostname` del referrer con el que se compara. */
  hostname: string
  /** `document.referrer`: URL completa o cadena vacía. */
  referrer: string
}

type TouchKind = 'campaign' | 'referral' | 'direct'

interface Touch {
  kind: TouchKind
  attribution: MarketingAttribution
  capturedAt: number
}

const TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000
const CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'gclid'] as const
const POSTHOG_CAMPAIGN_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term'] as const

/**
 * Referrers que no son marketing: la vuelta del checkout de pago y de los
 * logins de terceros. Atribuir la compra a `checkout.stripe.com` borraría la
 * campaña de verdad justo en la página de éxito.
 */
const NON_MARKETING_REFERRER_HOSTS = ['stripe.com', 'dodopayments.com', 'accounts.google.com', 'appleid.apple.com']

/** La landing de ESTA carga de página. Sólo vive en memoria hasta que hay consentimiento. */
let pageTouch: Touch | null = null

function isNonMarketingHost(host: string): boolean {
  return NON_MARKETING_REFERRER_HOSTS.some((blocked) => host === blocked || host.endsWith(`.${blocked}`))
}

function externalReferrerHost(visit: LandingVisit): string | null {
  try {
    const host = new URL(visit.referrer).hostname.toLowerCase()
    return host && host !== visit.hostname.toLowerCase() && !isNonMarketingHost(host) ? host : null
  } catch {
    return null
  }
}

function touchOf(visit: LandingVisit, now: number): Touch | null {
  if (classifyAnalyticsUrl({ pathname: visit.pathname, search: visit.search, hash: '' }) === 'credential') return null

  const params = new URLSearchParams(visit.search)
  const referrerHost = externalReferrerHost(visit)
  const attribution = parseMarketingAttribution({
    ...Object.fromEntries(CAMPAIGN_PARAMS.map((key) => [key, params.get(key)])),
    // Una entrega compartida SÍ es landing (trae tráfico); el saneado canónico le quita el token (ADR-055).
    landing_path: sanitizeAnalyticsUrl(visit.pathname),
    referrer_host: referrerHost,
  })
  const isCampaign = CAMPAIGN_PARAMS.some((key) => attribution[key] !== null)
  const kind: TouchKind = isCampaign ? 'campaign' : referrerHost ? 'referral' : 'direct'
  return { kind, attribution, capturedAt: now }
}

/** Último toque no directo: lo directo sólo cuenta si no había nada. */
function latestTouch(previous: Touch | null, next: Touch | null): Touch | null {
  if (!next) return previous
  if (next.kind === 'direct' && previous) return previous
  return next
}

function readStoredTouch(now: number): Touch | null {
  try {
    const stored = JSON.parse(getStorageItem(STORAGE_KEYS.MARKETING_ATTRIBUTION) ?? 'null') as Partial<Touch> | null
    if (!stored || typeof stored.capturedAt !== 'number' || now - stored.capturedAt > TOUCH_TTL_MS) return null
    const kind = stored.kind === 'campaign' || stored.kind === 'referral' ? stored.kind : 'direct'
    return { kind, attribution: parseMarketingAttribution(stored.attribution), capturedAt: stored.capturedAt }
  } catch {
    return null
  }
}

function currentVisit(): LandingVisit {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hostname: window.location.hostname,
    referrer: document.referrer,
  }
}

/** Apunta la landing de esta carga de página. Se llama una vez, al arrancar el cliente. */
export function recordLandingVisit(visit: LandingVisit = currentVisit(), now = Date.now()): void {
  pageTouch = latestTouch(pageTouch, touchOf(visit, now))
  persistMarketingAttribution(now)
}

/**
 * Arranque del cliente (`instrumentation-client.ts`): apunta la URL real de la
 * página. Nunca lanza: la analítica no puede tumbar el arranque de la app.
 */
export function recordLandingVisitOnBoot(): void {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return
    recordLandingVisit()
  } catch {
    // Sin landing apuntada, la compra sale sin atribución: no es un error de la app.
  }
}

/** Guarda la landing de esta carga sobre lo guardado. No hace nada sin consentimiento. */
export function persistMarketingAttribution(now = Date.now()): void {
  if (!hasStoredAnalyticsConsent()) return
  const touch = latestTouch(readStoredTouch(now), pageTouch)
  if (touch) setStorageItem(STORAGE_KEYS.MARKETING_ATTRIBUTION, JSON.stringify(touch))
}

/** Borra lo guardado (consentimiento rechazado o retirado). */
export function clearMarketingAttribution(): void {
  removeStorageItem(STORAGE_KEYS.MARKETING_ATTRIBUTION)
}

/**
 * El fragmento del cuerpo del checkout: `{ attribution }` o `{}` sin
 * consentimiento o sin toque. Se esparce en el cuerpo (`{ packageId,
 * ...checkoutAttributionField() }`) y así `JSON.stringify` no manda la clave
 * cuando no hay nada que atribuir.
 *
 * Va en el cuerpo del checkout porque `purchase_completed` se emite en el
 * webhook, sin navegador: es lo único que lleva la atribución hasta él.
 *
 * Lectura PURA: no escribe ni borra. Guardar y borrar es cosa de las
 * transiciones de consentimiento (`useCookieConsent`) y de `recordLandingVisit`.
 */
export function checkoutAttributionField(now = Date.now()): { attribution?: MarketingAttribution } {
  if (!hasStoredAnalyticsConsent()) return {}
  const touch = latestTouch(readStoredTouch(now), pageTouch)
  return touch ? { attribution: touch.attribution } : {}
}

/** UTM no vacías para registrar como super-properties de PostHog tras consentir. */
export function postHogCampaignProperties(): Partial<Pick<MarketingAttribution, (typeof POSTHOG_CAMPAIGN_PARAMS)[number]>> {
  const attribution = checkoutAttributionField().attribution
  if (!attribution) return {}

  const properties: Partial<Pick<MarketingAttribution, (typeof POSTHOG_CAMPAIGN_PARAMS)[number]>> = {}
  for (const key of POSTHOG_CAMPAIGN_PARAMS) {
    const value = attribution[key]
    if (value !== null) properties[key] = value
  }
  return properties
}
